import type {
    CatalogItemFull,
    CreateBookingDetails,
} from "@albertine/types/openapi/lmt";
import type { Booking } from "./common/types/Booking";
export type ToastMessageInfo = {
    type: "info" | "error";
    message: string;
};

export type ToastMessageNotification = {
    type: "notification";
    title: string;
    subtitle: string;
    agent?: string | undefined;
    member?: string | undefined;
    link: string;
    color?: string | undefined;
    deadlineAt?: Date | undefined;
};

// Shown when a newer build of LMT has been deployed. Clicking it reloads the
// page so the agent picks up the new version.
export type ToastMessageUpdate = {
    type: "update";
};

export type ToastMessage =
    | ToastMessageInfo
    | ToastMessageNotification
    | ToastMessageUpdate;

type ToastMessageEmitter = {
    name: "showToastMessage";
    params: ToastMessage;
    listener: (params: ToastMessage) => void;
};

type AddFilesToChatAttachmentsEmitter = {
    name: "addFilesToChatAttachments";
    params: { files: FileList };
    listener: (params: { files: FileList }) => void;
};

type AddBookingToChatAttachmentsEmitter = {
    name: "addBookingToChatAttachments";
    params: { booking: Booking };
    listener: (params: { booking: Booking }) => void;
};

type AddCatalogItemToChatAttachmentsEmitter = {
    name: "addCatalogItemToChatAttachments";
    params: { item: CatalogItemFull };
    listener: (params: { item: CatalogItemFull }) => void;
};

type AddMessageToChatReplyEmitter = {
    name: "addMessageToChatReply";
    params: { message: string };
    listener: (params: { message: string }) => void;
};

type ChangeChatContextEmitter = {
    name: "changeChatContextKey";
    params: { id: string };
    listener: (params: { id: string }) => void;
};

type ToggleThreadedChatEmitter = {
    name: "toggleThreadedChat";
    params: { enabled: boolean };
    listener: (params: { enabled: boolean }) => void;
};

type ScrollChatToMessageEmitter = {
    name: "scrollChatIntoMessage";
    params: { messageId: string };
    listener: (params: { messageId: string }) => void;
};

type CatalogItemEditedEmitter = {
    name: "catalogItemEdited";
    params: {
        item: Pick<
            CatalogItemFull,
            | "id"
            | "name"
            | "images"
            | "offeringCategories"
            | "addressComponents"
            | "locationGeoPoint"
        >;
    };
    listener: (params: {
        item: Pick<
            CatalogItemFull,
            | "id"
            | "name"
            | "images"
            | "offeringCategories"
            | "addressComponents"
            | "locationGeoPoint"
        >;
    }) => void;
};

type CatalogItemDeletedEmitter = {
    name: "catalogItemDeleted";
    params: { item: string };
    listener: (params: { item: string }) => void;
};

type AddCartItemEmitter = {
    name: "addCatalogCartItem";
    params: {
        item: CatalogItemFull;
        recommendation?: {
            fitScore: number;
            rank: number;
        };
    };
    listener: (params: {
        item: CatalogItemFull;
        recommendation?: {
            fitScore: number;
            rank: number;
        };
    }) => void;
};

export type CreateOneOffBookingInput = {
    details: CreateBookingDetails;
    photoUrls?: string[];
    recommendation?: { optionId: string; fitScore: number };
};

type AddOneOffBookingEmitter = {
    name: "createOneOffBooking";
    params: { input?: CreateOneOffBookingInput };
    listener: (params: { input?: CreateOneOffBookingInput }) => void;
};

type CollapseOldAiSummariesEmitter = {
    name: "collapseOldAiSummaries";

    params: {};

    listener: (params: {}) => void;
};

type Event =
    | Pick<ToastMessageEmitter, "name" | "params">
    | Pick<AddFilesToChatAttachmentsEmitter, "name" | "params">
    | Pick<AddBookingToChatAttachmentsEmitter, "name" | "params">
    | Pick<AddCatalogItemToChatAttachmentsEmitter, "name" | "params">
    | Pick<AddMessageToChatReplyEmitter, "name" | "params">
    | Pick<ChangeChatContextEmitter, "name" | "params">
    | Pick<ToggleThreadedChatEmitter, "name" | "params">
    | Pick<ScrollChatToMessageEmitter, "name" | "params">
    | Pick<CatalogItemEditedEmitter, "name" | "params">
    | Pick<CatalogItemDeletedEmitter, "name" | "params">
    | Pick<AddCartItemEmitter, "name" | "params">
    | Pick<AddOneOffBookingEmitter, "name" | "params">
    | Pick<CollapseOldAiSummariesEmitter, "name" | "params">;

export type EventListener =
    | Pick<ToastMessageEmitter, "name" | "listener">
    | Pick<AddFilesToChatAttachmentsEmitter, "name" | "listener">
    | Pick<AddBookingToChatAttachmentsEmitter, "name" | "listener">
    | Pick<AddCatalogItemToChatAttachmentsEmitter, "name" | "listener">
    | Pick<AddMessageToChatReplyEmitter, "name" | "listener">
    | Pick<ChangeChatContextEmitter, "name" | "listener">
    | Pick<ToggleThreadedChatEmitter, "name" | "listener">
    | Pick<ScrollChatToMessageEmitter, "name" | "listener">
    | Pick<CatalogItemEditedEmitter, "name" | "listener">
    | Pick<CatalogItemDeletedEmitter, "name" | "listener">
    | Pick<AddCartItemEmitter, "name" | "listener">
    | Pick<AddOneOffBookingEmitter, "name" | "listener">
    | Pick<CollapseOldAiSummariesEmitter, "name" | "listener">;

const EventEmitter = (() => {
    let eventListeners: EventListener[] = [];

    return {
        addListener: (eventListener: EventListener): void => {
            eventListeners.push(eventListener);
        },

        removeListener: (eventListenerToRemove: EventListener): void => {
            eventListeners = eventListeners.filter(
                (eventListener) =>
                    eventListener.listener !== eventListenerToRemove.listener,
            );
        },

        emit: (event: Event): void => {
            const { name, params } = event;

            eventListeners
                .filter((el) => el.name === name)
                .forEach((eventListener: EventListener) => {
                    eventListener.listener(params as never);
                });
        },
    };
})();

export { EventEmitter };
